import type { ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import type { InformationFieldParams, InformationSeparatorParams } from 'korint-sdk';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  DataTable,
  DataTableColumnHeader,
  type DataTableFilter,
  facetedFilterFn,
} from '../../ui/data-table';
import { useCustomFieldConfigs } from '../InformationCard/useCustomFieldConfigs';
import { resolveFieldShortcutFromConfig } from '../InformationCard/InformationCard';
import { Group } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export type InformationTableEntity = {
  id: string;
  name?: string;
  customFields: Record<string, unknown>;
};

type InformationTableProps = {
  title: ReactNode;
  productId: string;
  entityType: 'customer' | 'policy' | 'asset' | 'brokerageFirm';
  fieldsConfig: Array<string | InformationFieldParams | InformationSeparatorParams>;
  entities: InformationTableEntity[];
  /** Columns appended after the field columns (e.g. status, dates). */
  extraColumns?: ColumnDef<InformationTableEntity>[];
  /** Filters for the extra columns; faceted filters for the field columns are added automatically. */
  filters?: DataTableFilter[];
  /** Rendered on the right of the card title (e.g. an edit-contract button). */
  headerAction?: ReactNode;
};

const isFieldEntry = (
  field: string | InformationFieldParams | InformationSeparatorParams,
): field is string | InformationFieldParams =>
  typeof field === 'string' || field.type !== 'separator';

export function InformationTable({
  title,
  productId,
  entityType,
  fieldsConfig,
  entities,
  extraColumns = [],
  filters = [],
  headerAction,
}: InformationTableProps) {
  const { t } = useTranslation();
  const customFieldConfigs = useCustomFieldConfigs({ productId, entityType });

  const resolvedFields = fieldsConfig.filter(isFieldEntry).flatMap(field => {
    const key = typeof field === 'string' ? field : field.key;
    const resolved = resolveFieldShortcutFromConfig(field, customFieldConfigs);
    if (!resolved) {
      return [];
    }
    const columnTitle = t([
      `${productId}.fields.${key}.label`,
      `fields.${key}.informationField.label`,
      `fields.${key}.label`,
      key,
    ]);
    return [{ key, resolved, columnTitle }];
  });

  const fieldColumns = resolvedFields.map(
    ({ key, resolved, columnTitle }): ColumnDef<InformationTableEntity> => ({
      id: key,
      accessorFn: row => row.customFields[key],
      filterFn: facetedFilterFn,
      header: ({ column }) => <DataTableColumnHeader column={column} title={columnTitle} />,
      cell: ({ getValue }) => {
        const value = getValue<unknown>();
        if (value == null || value === '') {
          return (
            <Text inline tone="muted">
              —
            </Text>
          );
        }
        if (resolved.type === 'select') {
          return t([`fields.${key}.options.${value}`, `options.${value}`, String(value)]);
        }
        if (resolved.type === 'boolean') {
          return t(value ? 'common.yes' : 'common.no');
        }
        return String(value);
      },
    }),
  );

  // Facet every field column except the first, which is the identifier (unique per row).
  const fieldFilters: DataTableFilter[] = resolvedFields
    .slice(1)
    .map(({ key, columnTitle }) => ({ columnId: key, title: columnTitle, type: 'faceted' }));

  return (
    <Card>
      <CardHeader>
        <Group justify="between" gap={2}>
          <CardTitle>{title}</CardTitle>
          {headerAction}
        </Group>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={[...fieldColumns, ...extraColumns]}
          data={entities}
          dense
          searchable
          filters={[...filters, ...fieldFilters]}
          getRowId={row => row.id}
        />
      </CardContent>
    </Card>
  );
}
